/* eslint-disable max-lines */
import { useEffect, useMemo } from 'react';
import ReactPlayer from 'react-player/lazy';
import { AnimatePresence, motion } from 'framer-motion';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, MoveDown, MoveUp, Mic } from 'lucide-react';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { useThemeVars } from '@utils/hooks/use-theme-vars.ts';
import { useSharedCaption } from '@utils/providers/shared-caption-context.tsx';
import { useAudio } from '@utils/providers/audio-context.tsx';
import { Tooltip } from '@components/atoms/ui-kit/tooltip.tsx';
import audioThumbnail from '@assets/images/audio-thumbnail.png';
import { shortcutLabel } from '@utils/editors/shortcut-label.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import Button from '@components/atoms/ui-kit/button.tsx';

// Position is 0-100 percentage: 0 = top, 100 = bottom (default)
const POSITION_STEP = 10;

/* eslint-disable no-restricted-syntax */
const colors = [
  { id: 1, value: '#fff', label: 'White' },
  { id: 2, value: '#ffd700', label: 'Yellow' },
  { id: 3, value: '#72f672', label: 'Green' }
];
/* eslint-enable no-restricted-syntax */

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const SharedCaptionStyleModal = ({ isOpen, onClose }: Props) => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const { cls } = useThemeVars();

  const {
    session,
    applyStyle,
    document: myDocument,
    setClickedIndex
  } = useSharedCaption();
  const { currentTime, isPlaying, playbackRate, isMuted } = useAudio();

  const styleOptions: {
    value: 'bold' | 'italic' | 'underlined' | 'strike';
    label: string;
    i18nKey: string;
    shortcut: string;
  }[] = [
    { value: 'bold', label: 'B', i18nKey: 'bold', shortcut: 'Ctrl+B' },
    { value: 'italic', label: 'I', i18nKey: 'italic', shortcut: 'Ctrl+I' },
    {
      value: 'underlined',
      label: 'U',
      i18nKey: 'underline',
      shortcut: 'Ctrl+U'
    },
    {
      value: 'strike',
      label: 'S',
      i18nKey: 'strikethrough',
      shortcut: 'Ctrl+S'
    }
  ];

  const handleColorSelect = (color: string) => {
    applyStyle('color', color);
  };

  const isAudio = useMemo(
    () => session?.media_type === 'audio' || !session?.media_url,
    [session]
  );

  // Get the current caption index and segment at playback time, or fall back to first
  const { currentSegment, currentSegmentIndex } = useMemo(() => {
    if (!myDocument?.document?.length) {
      return { currentSegment: null, currentSegmentIndex: -1 };
    }

    const currentTimeMs = currentTime * 1000;
    const idx = myDocument.document.findIndex(
      i => i.start <= currentTimeMs && i.stop >= currentTimeMs
    );

    // If there's a caption at current time, use it
    if (idx >= 0) {
      return {
        currentSegment: myDocument.document[idx],
        currentSegmentIndex: idx
      };
    }

    // Otherwise, use the first caption as a preview sample
    return {
      currentSegment: myDocument.document[0],
      currentSegmentIndex: 0
    };
  }, [myDocument?.document, currentTime]);

  // Get current position from segment (0-100, null = 100/bottom)
  const currentPosition = useMemo(() => {
    const pos = currentSegment?.position;
    if (pos === null || pos === undefined) return 100; // default = bottom
    // Handle both string and number (API returns int, but type may say string)
    const numPos = typeof pos === 'string' ? parseInt(pos, 10) : pos;
    if (isNaN(numPos)) return 100;
    return Math.max(0, Math.min(100, numPos));
  }, [currentSegment?.position]);

  // Position: 0 = top, 100 = bottom
  // Move "up" means decrease position value (towards top)
  const moveTextUp = () => {
    const newPosition = Math.max(currentPosition - POSITION_STEP, 0);
    applyStyle('position', newPosition.toString());
  };

  // Move "down" means increase position value (towards bottom)
  const moveTextDown = () => {
    const newPosition = Math.min(currentPosition + POSITION_STEP, 100);
    applyStyle('position', newPosition.toString());
  };

  const moveToTheTop = () => {
    applyStyle('position', '0');
  };

  const moveToTheBottom = () => {
    applyStyle('position', '100');
  };

  // Set clickedIndex when modal opens or segment changes so applyStyle targets the right segment
  useEffect(() => {
    if (isOpen && currentSegmentIndex >= 0) {
      setClickedIndex(currentSegmentIndex);
    }
  }, [isOpen, currentSegmentIndex, setClickedIndex]);

  const filteredItems = currentSegment ? [currentSegment] : [];

  // Check if a style is active on the current segment
  const isStyleActive = (
    style: 'bold' | 'italic' | 'underlined' | 'strike'
  ) => {
    return currentSegment?.[style] === true;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('caption-styling')}
      caption={t('adjust-how-subtitles-look')}
      size="large"
      customWidth="720px"
      footerActions={[
        <Button
          key="done"
          onClick={onClose}
          label={t('done')}
          size="small"
          variant="primary"
        />
      ]}
    >
      {/* Responsive layout: side-by-side on large, stacked on mobile/tablet */}
      <div className="flex flex-col lg:flex-row gap-3">
        {/* Video Preview */}
        <div
          className={cn(
            'relative w-full lg:w-[70%] rounded-2xl overflow-hidden border flex items-center justify-center',
            cls.border
          )}
          style={{ backgroundColor: getColour('secondLayer') }}
        >
          <div className="aspect-video w-full relative">
            {session?.media_url && !isAudio ? (
              <ReactPlayer
                url={session?.media_url}
                controls={false}
                playing={isPlaying}
                volume={1}
                muted={isMuted}
                playbackRate={playbackRate}
                width="100%"
                height="100%"
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                {isAudio ? (
                  <img src={audioThumbnail} alt="Audio" width={120} />
                ) : (
                  <Mic size={48} style={{ color: getColour('icon') }} />
                )}
              </div>
            )}

            {/* Caption Preview - inside aspect-video container */}
            {/* Position: 0% = top, 100% = bottom. Map to safe area (10%-90%) */}
            <div
              className="absolute left-0 right-0 py-2 px-4 flex justify-center"
              style={{
                top: `${10 + (currentPosition / 100) * 80}%`,
                transform: 'translateY(-50%)'
              }}
            >
              <AnimatePresence>
                {filteredItems?.map(item => (
                  <motion.div
                    key={item.start}
                    className="text-center px-3 py-1 rounded-2xl max-w-[80%] text-sm"
                    style={{
                      color: item.color || 'white',
                      backgroundColor: 'rgba(0, 0, 0, 0.75)',
                      fontWeight: item.bold ? 'bold' : 'normal',
                      textDecoration: `${item.underlined ? 'underline ' : ''}${
                        item.strike ? 'line-through' : ''
                      }`,
                      fontStyle: item.italic ? 'italic' : 'normal'
                    }}
                    dangerouslySetInnerHTML={{
                      __html: item.text.replace(/\n/g, '<br>')
                    }}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  />
                ))}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Styling Tools */}
        <div className="w-full lg:w-[30%] flex flex-col gap-2">
          {/* Text Style */}
          <div
            className="p-3 rounded-2xl border"
            style={{
              backgroundColor: getColour('secondLayer'),
              borderColor: getColour('border')
            }}
          >
            <label
              className="block text-[10px] font-medium mb-0.5 uppercase tracking-wider"
              style={{ color: getColour('inactiveText') }}
            >
              {t('text-style')}
            </label>
            <p
              className="text-[11px] mb-2 leading-tight"
              style={{ color: getColour('inactiveText') }}
            >
              {t('apply-formatting-to-selected-text')}
            </p>
            <div className="flex items-center gap-1">
              {styleOptions.map(style => {
                const isActive = isStyleActive(style.value);
                return (
                  <Tooltip
                    key={style.value}
                    label={shortcutLabel(t(style.i18nKey), style.shortcut)}
                  >
                    <button
                      onClick={() => applyStyle(style.value)}
                      className={cn(
                        'h-7 w-7 rounded-2xl flex items-center justify-center text-[11px] font-medium transition-all border',
                        'hover:bg-black/5 dark:hover:bg-white/10',
                        {
                          'font-bold': style.value === 'bold',
                          italic: style.value === 'italic',
                          underline: style.value === 'underlined',
                          'line-through': style.value === 'strike'
                        }
                      )}
                      style={{
                        color: getColour('normalText'),
                        borderColor: isActive
                          ? getColour('activeText')
                          : getColour('border'),
                        backgroundColor: isActive
                          ? getColour('thirdLayer')
                          : undefined
                      }}
                    >
                      {style.label}
                    </button>
                  </Tooltip>
                );
              })}
            </div>
          </div>

          {/* Color */}
          <div
            className="p-3 rounded-2xl border"
            style={{
              backgroundColor: getColour('secondLayer'),
              borderColor: getColour('border')
            }}
          >
            <label
              className="block text-[10px] font-medium mb-0.5 uppercase tracking-wider"
              style={{ color: getColour('inactiveText') }}
            >
              {t('text-color')}
            </label>
            <p
              className="text-[11px] mb-2 leading-tight"
              style={{ color: getColour('inactiveText') }}
            >
              {t('change-caption-text-color')}
            </p>
            <div className="flex items-center gap-1.5">
              {colors.map(color => {
                const isColorActive = currentSegment?.color === color.value;
                return (
                  <Tooltip key={color.value} label={color.label}>
                    <button
                      onClick={() => handleColorSelect(color.value)}
                      className={cn(
                        'w-6 h-6 rounded-full transition-all',
                        'hover:scale-110 active:scale-95',
                        isColorActive
                          ? 'ring-2 ring-offset-2 ring-blue-500'
                          : ''
                      )}
                      style={{
                        backgroundColor: color.value,
                        border:
                          color.id === 1
                            ? `2px solid ${getColour('border')}`
                            : `2px solid ${color.value}`
                      }}
                    />
                  </Tooltip>
                );
              })}
            </div>
          </div>

          {/* Position */}
          <div
            className="p-3 rounded-2xl border"
            style={{
              backgroundColor: getColour('secondLayer'),
              borderColor: getColour('border')
            }}
          >
            <label
              className="block text-[10px] font-medium mb-0.5 uppercase tracking-wider"
              style={{ color: getColour('inactiveText') }}
            >
              {t('position')}
            </label>
            <p
              className="text-[11px] mb-2 leading-tight"
              style={{ color: getColour('inactiveText') }}
            >
              {t('adjust-caption-vertical-position')}
            </p>
            <div className="flex items-center gap-1">
              {[
                {
                  icon: MoveUp,
                  action: moveToTheTop,
                  label: t('move-up-to-the-top'),
                  disabled: currentPosition <= 0
                },
                {
                  icon: ChevronUp,
                  action: moveTextUp,
                  label: t('move-up-one-level'),
                  disabled: currentPosition <= 0
                },
                {
                  icon: ChevronDown,
                  action: moveTextDown,
                  label: t('move-down-one-level'),
                  disabled: currentPosition >= 100
                },
                {
                  icon: MoveDown,
                  action: moveToTheBottom,
                  label: t('move-down-to-the-bottom'),
                  disabled: currentPosition >= 100
                }
              ].map(({ icon: Icon, action, label, disabled }, i) => (
                <Tooltip key={i} label={label}>
                  <button
                    onClick={action}
                    disabled={disabled}
                    className={cn(
                      'h-7 w-7 rounded-2xl flex items-center justify-center transition-all border',
                      'hover:bg-black/5 dark:hover:bg-white/10 active:scale-95',
                      'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent'
                    )}
                    style={{
                      color: getColour('icon'),
                      borderColor: getColour('border'),
                      backgroundColor: getColour('thirdLayer')
                    }}
                  >
                    <Icon size={14} />
                  </button>
                </Tooltip>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
